import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  classifyScheduleStatus,
  scheduleFailureReason,
  scheduleNeedsAttention,
} from '@canopy/shared';
import { ChevronDown, KebabIcon } from '../../components/icons.js';
import { ScheduleFields, cronToHuman, tzLabel } from '../../components/schedule-fields.js';
import {
  useAllSchedules,
  useToggleSchedule,
  useRunScheduleNow,
  useDeleteSchedule,
  useUpdateSchedule,
  useScheduleDeliveries,
  type Schedule,
  type DeliveryConfig,
  type ScheduleFormat,
} from '../../api/schedules.js';

/**
 * SubscriptionsManager — admin view of scheduled report deliveries
 * (subscriptions), embeddable in the Insights viewer.
 *
 * The list is scope-filtered server-side (a customer admin sees only their
 * subtree's subscriptions; an internal admin sees the whole tenant). Styling
 * follows the Canopy design standards: `.data-table` with zebra striping,
 * tokenized type/colour, 24-hour times, and — per standards/destructive-
 * actions.md — no inline delete; a per-row kebab menu with a named
 * confirmation modal instead.
 */
type StatusFilter = 'all' | 'enabled' | 'disabled' | 'failing';
type SortKey = 'name' | 'report' | 'owner' | 'createdBy' | 'nextRunAt' | 'status';

interface EditableChannel {
  kind: string;
  recipients?: string[];
  [k: string]: unknown;
}

export function SubscriptionsManager(): JSX.Element {
  const { data: schedules, isLoading, error } = useAllSchedules();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [query, setQuery] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Schedule | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'nextRunAt', dir: 1 });

  const toggle = useToggleSchedule();
  const runNow = useRunScheduleNow();
  const del = useDeleteSchedule();
  // CSAGE-258: surface pause/run-now/delete failures. Previously these
  // mutations swallowed 404/500s silently, so the buttons looked dead.
  const actionErr = (toggle.error ?? runNow.error ?? del.error) as Error | null;

  const list: Schedule[] = useMemo(() => (schedules as Schedule[] | undefined) ?? [], [schedules]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = list;
    if (statusFilter === 'enabled') out = out.filter((s) => s.enabled);
    else if (statusFilter === 'disabled') out = out.filter((s) => !s.enabled);
    else if (statusFilter === 'failing')
      out = out.filter((s) => s.enabled && scheduleNeedsAttention(s.lastStatus));
    if (q)
      out = out.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          (s.artifactName ?? '').toLowerCase().includes(q) ||
          // CSAGE-385: "which subscriptions did this person set up?" is the
          // support question that opened the ticket — make it searchable.
          (s.createdBy ?? '').toLowerCase().includes(q) ||
          channelRecipients(s).join(' ').toLowerCase().includes(q),
      );
    return out;
  }, [list, statusFilter, query]);

  // Client-side sort per the kit table standard (sortable column headers).
  const sorted = useMemo(() => {
    const key = sort.key;
    const val = (s: Schedule): string => {
      switch (key) {
        case 'name':
          return s.name.toLowerCase();
        case 'report':
          return (s.artifactName ?? '').toLowerCase();
        case 'owner':
          return (s.ownerEmail ?? '').toLowerCase();
        case 'createdBy':
          return (s.createdBy ?? '').toLowerCase();
        case 'nextRunAt':
          return s.nextRunAt ?? '9999';
        case 'status':
          return (s.enabled ? '1' : '0') + (s.lastStatus ?? '');
      }
    };
    return [...filtered].sort((a, b) => sort.dir * val(a).localeCompare(val(b)));
  }, [filtered, sort]);

  function toggleSort(key: SortKey): void {
    setSort((cur) => (cur.key === key ? { key, dir: cur.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  }

  const detail = detailId ? (list.find((s) => s.id === detailId) ?? null) : null;

  const stats = useMemo(
    () => ({
      all: list.length,
      enabled: list.filter((s) => s.enabled).length,
      disabled: list.filter((s) => !s.enabled).length,
      failing: list.filter((s) => s.enabled && scheduleNeedsAttention(s.lastStatus)).length,
    }),
    [list],
  );

  return (
    <div className="subs-manager">
      <header className="subs-header">
        <div>
          <h2 className="subs-title">Subscriptions</h2>
          <p className="subs-subtitle">Scheduled report deliveries you administer.</p>
        </div>
        <div className="subs-header-badges">
          <span className="badge badge--success">{stats.enabled} active</span>
          {stats.failing > 0 && <span className="badge badge--danger">{stats.failing} errors</span>}
          {stats.disabled > 0 && (
            <span className="badge badge--neutral">{stats.disabled} suspended</span>
          )}
        </div>
      </header>

      <div className="subs-toolbar">
        <div className="subs-filters" role="tablist" aria-label="Filter by status">
          {(['all', 'enabled', 'disabled', 'failing'] as StatusFilter[]).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={statusFilter === v}
              className={'chip' + (statusFilter === v ? ' chip--on' : '')}
              onClick={() => setStatusFilter(v)}
            >
              {v[0]!.toUpperCase() + v.slice(1)}{' '}
              <span className="subs-filter-count">{stats[v]}</span>
            </button>
          ))}
        </div>
        <input
          type="search"
          className="subs-search"
          placeholder="Search report or recipient…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search subscriptions"
        />
      </div>

      {isLoading && <div className="subs-note">Loading subscriptions…</div>}
      {error && <div className="alert alert--danger">Failed to load subscriptions.</div>}
      {actionErr && (
        <div className="alert alert--danger" role="alert">
          Action failed{actionErr.message ? `: ${actionErr.message}` : ''}. Please retry.
        </div>
      )}
      {schedules && filtered.length === 0 && !isLoading && (
        <div className="empty-state" style={{ border: 'none' }}>
          <h3>No subscriptions</h3>
          <p className="text-dim">
            {list.length === 0
              ? 'No scheduled deliveries exist for your scope yet.'
              : 'None match this filter.'}
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="subs-table-wrap">
          <table className="data-table data-table--zebra subs-table">
            <thead>
              <tr>
                <SortTh label="Subscription" k="name" sort={sort} onSort={toggleSort} />
                <SortTh label="Report" k="report" sort={sort} onSort={toggleSort} />
                {/* CSAGE-385: two columns, because these are two different
                    facts. "Created by" is who set the subscription up (the
                    schedule's own created_by); "Owner" is who owns the report
                    being delivered. Resolving the first from the second is the
                    bug — it attributed a Mood Media user's subscription to the
                    Banyan engineer who had last edited the shared report. */}
                <SortTh label="Created by" k="createdBy" sort={sort} onSort={toggleSort} />
                <SortTh label="Report owner" k="owner" sort={sort} onSort={toggleSort} />
                <th>Schedule</th>
                <th>Recipients</th>
                <SortTh label="Next run" k="nextRunAt" sort={sort} onSort={toggleSort} />
                <SortTh label="Status" k="status" sort={sort} onSort={toggleSort} />
                <th className="cell-align-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((s) => (
                <SubRow
                  key={s.id}
                  schedule={s}
                  onOpen={() => setDetailId(s.id)}
                  onToggleEnabled={(enabled) => toggle.mutate({ id: s.id, enabled })}
                  onRunNow={() => runNow.mutate(s.id)}
                  onDelete={() => setPendingDelete(s)}
                  runBusy={runNow.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <SubscriptionDetailModal
          schedule={detail}
          runBusy={runNow.isPending}
          onClose={() => setDetailId(null)}
          onToggleEnabled={(enabled) => toggle.mutate({ id: detail.id, enabled })}
          onRunNow={() => runNow.mutate(detail.id)}
          onDelete={() => setPendingDelete(detail)}
        />
      )}

      {pendingDelete && (
        <ConfirmDeleteDialog
          schedule={pendingDelete}
          busy={del.isPending}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            del.mutate(pendingDelete.id, { onSuccess: () => setPendingDelete(null) });
          }}
        />
      )}
    </div>
  );
}

/** Sortable column header per the kit table standard (arrow on the active
 *  sort column; aria-sort for screen readers). */
function SortTh({
  label,
  k,
  sort,
  onSort,
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; dir: 1 | -1 };
  onSort(k: SortKey): void;
}): JSX.Element {
  const active = sort.key === k;
  return (
    <th aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
      <button type="button" className="th-sort" onClick={() => onSort(k)}>
        <span>{label}</span>
        <ChevronDown
          size={10}
          className={'th-sort-arrow' + (active ? ' th-sort-arrow--on' : '')}
          style={{ transform: active && sort.dir === -1 ? 'rotate(180deg)' : 'none' }}
        />
      </button>
    </th>
  );
}

function SubRow({
  schedule: s,
  onOpen,
  onToggleEnabled,
  onRunNow,
  onDelete,
  runBusy,
}: {
  schedule: Schedule;
  onOpen(): void;
  onToggleEnabled(enabled: boolean): void;
  onRunNow(): void;
  onDelete(): void;
  runBusy: boolean;
}): JSX.Element {
  const recips = channelRecipients(s);
  return (
    <tr className="subs-row" onClick={onOpen}>
      <td>
        <button type="button" className="catalog-link" onClick={onOpen}>
          {s.name}
        </button>
      </td>
      <td>
        <div className="subs-report">{s.artifactName ?? em()}</div>
        {s.artifactScope && <div className="subs-report-scope">{s.artifactScope}</div>}
      </td>
      {/* Pre-CSAGE-385 subscriptions have no recorded creator and are not
          backfillable (the audit trail nulls its actor for portal identities —
          CSAGE-367), so an en dash here is the honest answer, per
          content-style.md §6. */}
      <td className="subs-muted">{s.createdBy ?? em()}</td>
      <td className="subs-muted">{s.ownerEmail ?? em()}</td>
      <td>
        <div className="subs-schedule">{cronToHuman(s.cron)}</div>
        <div className="subs-schedule-tz">{tzLabel(s.tz)}</div>
      </td>
      <td>
        <RecipientsCell recipients={recips} />
      </td>
      <td className="subs-muted">{fmtDate(s.nextRunAt)}</td>
      <td>
        <StatusBadge status={s.lastStatus} enabled={s.enabled} />
      </td>
      {/* stopPropagation: the kebab lives inside the clickable row. */}
      <td className="cell-align-right" onClick={(e) => e.stopPropagation()}>
        <KebabMenu
          enabled={s.enabled}
          runBusy={runBusy}
          onEdit={onOpen}
          onToggleEnabled={() => onToggleEnabled(!s.enabled)}
          onRunNow={onRunNow}
          onDelete={onDelete}
        />
      </td>
    </tr>
  );
}

/**
 * Detail modal — opened by clicking a row (or kebab → Edit). Replaces the
 * old inline expand-row: Overview facts up top, then Schedule, Recipients,
 * and Recent deliveries sections; actions in the footer.
 */
function SubscriptionDetailModal({
  schedule: s,
  runBusy,
  onClose,
  onToggleEnabled,
  onRunNow,
  onDelete,
}: {
  schedule: Schedule;
  runBusy: boolean;
  onClose(): void;
  onToggleEnabled(enabled: boolean): void;
  onRunNow(): void;
  onDelete(): void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal modal--detail subscription-editor-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subs-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header subs-detail-head">
          <div>
            <h2 id="subs-detail-title">{s.name}</h2>
            <p className="modal-subtitle">
              {s.artifactName ?? em()}
              {s.artifactScope ? ` · ${s.artifactScope}` : ''}
            </p>
          </div>
          <div className="subs-detail-headright">
            <StatusBadge status={s.lastStatus} enabled={s.enabled} />
            <button type="button" className="btn-icon" aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>
        <div className="modal-body subs-detail-body">
          <dl className="subs-overview">
            <div>
              <dt>Created by</dt>
              <dd>{s.createdBy ?? em()}</dd>
            </div>
            {/* CSAGE-384: the scope the subscription actually RENDERS AT. Shown
                next to the report's own scope because when they differ, that
                difference is the answer to "why is this delivering the wrong
                data?" — and until now nothing could read it back at all. */}
            <div>
              <dt>Runs at scope</dt>
              <dd>{s.scopePath ?? em()}</dd>
            </div>
            <div>
              <dt>Report owner</dt>
              <dd>{s.ownerEmail ?? em()}</dd>
            </div>
            <div>
              <dt>Format</dt>
              <dd className="subs-upper">{s.delivery?.format ?? em()}</dd>
            </div>
            <div>
              <dt>Next run</dt>
              <dd>{fmtDate(s.nextRunAt)}</dd>
            </div>
            <div>
              <dt>Last run</dt>
              <dd>{fmtDate(s.lastRunAt)}</dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{fmtDate(s.createdAt)}</dd>
            </div>
            <div>
              <dt>Timezone</dt>
              <dd>{tzLabel(s.tz)}</dd>
            </div>
          </dl>
          <SubEditor schedule={s} />
        </div>
        <div className="modal-footer subs-detail-foot">
          <button type="button" className="btn btn--ghost btn--danger-text" onClick={onDelete}>
            Delete
          </button>
          <div className="subs-detail-footright">
            <button type="button" className="btn" disabled={runBusy} onClick={onRunNow}>
              Run now
            </button>
            <button type="button" className="btn" onClick={() => onToggleEnabled(!s.enabled)}>
              {s.enabled ? 'Disable' : 'Enable'}
            </button>
            <button type="button" className="btn btn--primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KebabMenu({
  enabled,
  runBusy,
  onEdit,
  onToggleEnabled,
  onRunNow,
  onDelete,
}: {
  enabled: boolean;
  runBusy: boolean;
  onEdit(): void;
  onToggleEnabled(): void;
  onRunNow(): void;
  onDelete(): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  /**
   * CSAGE-273: `.subs-table-wrap` is `overflow: hidden`, so an
   * absolutely-positioned menu was clipped at the container edge —
   * unreachable on the last row, and with a single row (any narrow
   * filter) the whole menu was cut off. Portal to <body> to escape the
   * clipping ancestor, then flip above the trigger when there isn't
   * room below so the viewport edge can't clip it either.
   *
   * The menu renders hidden on the first layout pass so we can measure
   * its real height; both passes run inside useLayoutEffect, before
   * paint, so there is no visible jump.
   */
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const btn = btnRef.current;
    const pop = popRef.current;
    if (!btn || !pop) return;
    const r = btn.getBoundingClientRect();
    const GAP = 4;
    const popH = pop.offsetHeight;
    const popW = pop.offsetWidth;
    const roomBelow = window.innerHeight - r.bottom;
    const flipUp = roomBelow < popH + GAP && r.top > popH + GAP;
    const top = flipUp ? r.top - popH - GAP : r.bottom + GAP;
    setPos({
      top: Math.max(GAP, Math.min(top, window.innerHeight - popH - GAP)),
      left: Math.max(GAP, Math.min(r.right - popW, window.innerWidth - popW - GAP)),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      const t = e.target as Node | null;
      if (t && popRef.current?.contains(t)) return;
      if (t && btnRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    // A fixed-position menu would detach from its row on scroll/resize.
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  const run = (fn: () => void) => () => {
    close();
    fn();
  };

  const menu = open
    ? createPortal(
        <div
          ref={popRef}
          className="subs-menu subs-menu--portaled"
          role="menu"
          style={{
            position: 'fixed',
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <button type="button" role="menuitem" className="subs-menu-item" onClick={run(onEdit)}>
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="subs-menu-item"
            onClick={run(onToggleEnabled)}
          >
            {enabled ? 'Disable' : 'Enable'}
          </button>
          <button
            type="button"
            role="menuitem"
            className="subs-menu-item"
            disabled={runBusy}
            onClick={run(onRunNow)}
          >
            Run now
          </button>
          <div className="subs-menu-divider" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="subs-menu-item subs-menu-item--danger"
            onClick={run(onDelete)}
          >
            Delete
          </button>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="subs-kebab">
      <button
        ref={btnRef}
        type="button"
        className="btn-icon"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Subscription actions"
        onClick={() => setOpen((o) => !o)}
      >
        <KebabIcon size={16} />
      </button>
      {menu}
    </div>
  );
}

function ConfirmDeleteDialog({
  schedule,
  busy,
  onCancel,
  onConfirm,
}: {
  schedule: Schedule;
  busy: boolean;
  onCancel(): void;
  onConfirm(): void;
}): JSX.Element {
  return (
    <div className="modal-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="modal modal--confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="subs-del-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="subs-del-title">Delete subscription “{schedule.name}”?</h2>
        </div>
        <div className="modal-body">
          <p>
            Any in-progress delivery finishes, but no future runs will fire. This can’t be undone.
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn--danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecipientsCell({ recipients }: { recipients: string[] }): JSX.Element {
  if (recipients.length === 0) return <span className="subs-muted">{em()}</span>;
  const shown = recipients.slice(0, 2);
  const extra = recipients.length - shown.length;
  return (
    <span className="subs-recipients" title={recipients.join(', ')}>
      {shown.join(', ')}
      {extra > 0 && <span className="subs-recipients-more"> +{extra}</span>}
    </span>
  );
}

function SubEditor({ schedule: s }: { schedule: Schedule }): JSX.Element {
  const update = useUpdateSchedule();
  const [newRecip, setNewRecip] = useState<Record<number, string>>({});
  const channels = (s.delivery.channels ?? []) as unknown as EditableChannel[];

  function mutateRecipients(channelIndex: number, next: string[]): void {
    const nextChannels = channels.map((c, i) =>
      i === channelIndex ? { ...c, recipients: next } : c,
    );
    const nextDelivery = { ...s.delivery, channels: nextChannels } as unknown as DeliveryConfig;
    update.mutate({ id: s.id, patch: { delivery: nextDelivery } });
  }

  return (
    <div className="subs-editor">
      <DetailsEditor schedule={s} />

      <ScheduleEditor schedule={s} />

      <section className="subs-editor-section">
        <h4 className="subs-editor-h">Recipients</h4>
        {channels.length === 0 && <div className="subs-muted">No delivery channels.</div>}
        {channels.map((c, ci) =>
          Array.isArray(c.recipients) ? (
            <div key={ci} className="subs-chan">
              <div className="subs-chan-kind">{c.kind}</div>
              <div className="subs-chips">
                {c.recipients.map((r) => (
                  <span key={r} className="subs-recip-chip">
                    {r}
                    <button
                      type="button"
                      className="subs-recip-remove"
                      aria-label={`Remove ${r}`}
                      title={`Remove ${r}`}
                      onClick={() =>
                        mutateRecipients(
                          ci,
                          (c.recipients ?? []).filter((x) => x !== r),
                        )
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="email"
                  className="subs-input subs-recip-add"
                  placeholder="Add email…"
                  value={newRecip[ci] ?? ''}
                  onChange={(e) => setNewRecip((m) => ({ ...m, [ci]: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const v = (newRecip[ci] ?? '').trim();
                      if (v && !(c.recipients ?? []).includes(v)) {
                        mutateRecipients(ci, [...(c.recipients ?? []), v]);
                        setNewRecip((m) => ({ ...m, [ci]: '' }));
                      }
                    }
                  }}
                  aria-label={`Add recipient to ${c.kind}`}
                />
              </div>
            </div>
          ) : (
            <div key={ci} className="subs-muted subs-chan-fixed">
              {c.kind} — destination fixed (no editable recipient list).
            </div>
          ),
        )}
      </section>

      <DeliveriesPanel scheduleId={s.id} />
    </div>
  );
}

// ─── Details editor (CSAGE-290: edit name + format on an existing sub) ───────
//
// The detail modal already let you edit the schedule and recipients; name and
// format had no editor, so a subscription couldn't be renamed or its export
// format changed after creation. This closes that gap via the same
// useUpdateSchedule PATCH. (Its saves were also unblocked by CSAGE-289 — before
// that fix, a scoped user's PATCH on a template-artifact subscription 404'd.)

const FORMAT_OPTIONS: ReadonlyArray<{ value: ScheduleFormat; label: string }> = [
  { value: 'pdf', label: 'PDF' },
  { value: 'xlsx', label: 'Excel (XLSX)' },
  { value: 'csv', label: 'CSV' },
  { value: 'html', label: 'HTML' },
  { value: 'json', label: 'JSON (data only)' },
];

function DetailsEditor({ schedule: s }: { schedule: Schedule }): JSX.Element {
  const update = useUpdateSchedule();
  const curFormat = (s.delivery?.format ?? 'csv') as ScheduleFormat;
  const [name, setName] = useState(s.name);
  const [format, setFormat] = useState<ScheduleFormat>(curFormat);
  const nameTrim = name.trim();
  const nameValid = nameTrim.length > 0;
  const dirty = nameTrim !== s.name || format !== curFormat;

  function save(): void {
    if (!dirty || !nameValid) return;
    const patch: Parameters<typeof update.mutate>[0]['patch'] = {};
    if (nameTrim !== s.name) patch.name = nameTrim;
    if (format !== curFormat) {
      patch.delivery = { ...s.delivery, format } as DeliveryConfig;
    }
    update.mutate({ id: s.id, patch });
  }

  return (
    <section className="subs-editor-section">
      <h4 className="subs-editor-h">Details</h4>
      <label className="field">
        <span className="field-label">Name</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Subscription name"
          aria-invalid={!nameValid}
        />
      </label>
      <label className="field">
        <span className="field-label">Format</span>
        <select
          className="select"
          value={format}
          onChange={(e) => setFormat(e.target.value as ScheduleFormat)}
        >
          {FORMAT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <div className="subs-sched-actions">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={!dirty || !nameValid || update.isPending}
          onClick={save}
        >
          {update.isPending ? 'Saving…' : 'Save details'}
        </button>
        {!nameValid && <span className="subs-error">Name is required.</span>}
        {update.isError && <span className="subs-error">Save failed — please retry.</span>}
      </div>
    </section>
  );
}

// ─── Schedule editor (CSAGE-266: shared ScheduleFields builder) ─────────────

function ScheduleEditor({ schedule: s }: { schedule: Schedule }): JSX.Element {
  const update = useUpdateSchedule();
  const [cron, setCron] = useState(s.cron);
  const [tz, setTz] = useState(s.tz || 'UTC');
  const [valid, setValid] = useState(true);
  const dirty = cron !== s.cron || tz !== s.tz;

  return (
    <section className="subs-editor-section">
      <div className="subs-sched-head">
        <h4 className="subs-editor-h">Schedule</h4>
      </div>
      <ScheduleFields
        cron={s.cron}
        tz={s.tz || 'UTC'}
        onChange={(v) => {
          setCron(v.cron);
          setTz(v.tz);
        }}
        onValidityChange={setValid}
      />
      <div className="subs-sched-actions">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={!dirty || !valid || update.isPending}
          onClick={() => valid && dirty && update.mutate({ id: s.id, patch: { cron, tz } })}
        >
          Save schedule
        </button>
        {update.isError && <span className="subs-error">Save failed — please retry.</span>}
      </div>
    </section>
  );
}

function DeliveriesPanel({ scheduleId }: { scheduleId: string }): JSX.Element {
  const { data: deliveries, isLoading } = useScheduleDeliveries(scheduleId);
  // CSAGE-588: full editor width — a half-width cell can't hold five columns
  // plus a delivery error, which overflowed the card.
  return (
    <section className="subs-editor-section subs-editor-section--wide">
      <h4 className="subs-editor-h">Recent deliveries</h4>
      {isLoading && <div className="subs-muted">Loading…</div>}
      {deliveries && deliveries.length === 0 && (
        <div className="subs-muted">No deliveries recorded yet.</div>
      )}
      {deliveries && deliveries.length > 0 && (
        <table className="data-table subs-deliveries">
          <thead>
            <tr>
              <th>Started</th>
              <th>Channel</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>Last error</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.slice(0, 10).map((d) => (
              <tr key={d.id}>
                <td className="subs-muted">{fmtDate(d.createdAt)}</td>
                <td>{d.channel}</td>
                <td>
                  <DeliveryStatusBadge status={d.status} />
                </td>
                <td>{d.attempts}</td>
                <td className="subs-error-cell">{d.lastError ?? em()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

function channelRecipients(s: Schedule): string[] {
  const channels = (s.delivery?.channels ?? []) as unknown as EditableChannel[];
  return channels.flatMap((c) => (Array.isArray(c.recipients) ? c.recipients : []));
}

// Status words + colours come from standards/status-taxonomy.md — do not invent.
// CSAGE-346: classify via @canopy/shared instead of comparing raw strings —
// this used to test `status === 'success'` while the runner writes 'succeeded',
// so every healthy subscription rendered as Error.
function StatusBadge({
  status,
  enabled,
}: {
  status: string | null;
  enabled: boolean;
}): JSX.Element {
  if (!enabled) return <span className="badge badge--warning">Suspended</span>;
  const state = classifyScheduleStatus(status);
  if (state === 'never') return <span className="badge badge--info">Scheduled</span>;
  if (state === 'succeeded') return <span className="badge badge--success">Active</span>;
  if (state === 'running') return <span className="badge badge--warning">Running</span>;
  if (state === 'partial') return <span className="badge badge--warning">Partial</span>;
  const reason = scheduleFailureReason(status);
  return (
    <span className="badge badge--danger" {...(reason ? { title: reason } : {})}>
      Error
    </span>
  );
}

function DeliveryStatusBadge({ status }: { status: string }): JSX.Element {
  if (status === 'sent') return <span className="badge badge--success">Finished</span>;
  if (status === 'pending') return <span className="badge badge--warning">Pending</span>;
  return <span className="badge badge--danger">Error</span>; // failed / dead
}

/** Empty-value glyph per Canopy standards (en dash). */
function em(): string {
  return '–';
}

/**
 * 24-hour date-time per standards/datetime.md (MMM D, YYYY, HH:mm), in the
 * viewer's zone. CSAGE-588: the zone is labeled because these sit beside the
 * schedule's own timezone ("Timezone UTC"), which an unlabeled local time
 * reads as. `timeZone` defaults to the viewer's; tests pin it.
 */
export function fmtDate(iso: string | null, timeZone?: string): string {
  if (!iso) return em();
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return em();
  return d.toLocaleString('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short',
  });
}

// tzLabel + cronToHuman now come from the shared schedule-fields module (CSAGE-266).
